/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Lazy load routes for PRPL pattern (Lazy-load remaining routes)
const About = lazy(() => import('./pages/About'));
const Service = lazy(() => import('./pages/Service'));
const Partners = lazy(() => import('./pages/Partners'));
const Contact = lazy(() => import('./pages/Contact'));
const Projects = lazy(() => import('./pages/Projects'));
const Interior = lazy(() => import('./pages/Interior'));
const ProjectDetail = lazy(() => import('./pages/ProjectDetail'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProjects = lazy(() => import('./pages/admin/AdminProjects'));
const AdminInterior = lazy(() => import('./pages/admin/AdminInterior'));
const AdminInquiries = lazy(() => import('./pages/admin/AdminInquiries'));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads'));
const AdminPartnerships = lazy(() => import('./pages/admin/AdminPartnerships'));
const SharedInquiry = lazy(() => import('./pages/public/SharedInquiry'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-8 h-8 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
  </div>
);

export default function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <Router>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/service" element={<Service />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/interior" element={<Interior />} />
              <Route path="/projects/:slug" element={<ProjectDetail />} />
              <Route path="/share/inquiry/:token" element={<SharedInquiry />} />
              
              {/* Admin Routes */}
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="projects" element={<AdminProjects />} />
                <Route path="interior" element={<AdminInterior />} />
                <Route path="inquiries" element={<AdminInquiries />} />
                <Route path="leads" element={<AdminLeads />} />
                <Route path="partnerships" element={<AdminPartnerships />} />
              </Route>
              
              {/* Catch-all route for Supabase Auth redirects */}
              <Route path="*" element={<Home />} />
            </Routes>
          </Suspense>
        </Router>
      </LanguageProvider>
    </AuthProvider>
  );
}
